/**
 * Tennis match format presets (sc-336 / Tennis Phase 2).
 *
 * This shape is deliberately data, not behavior — Phase 3's scoring engine
 * (`reduce(format, points[]) -> state`, per the spec) consumes it as a pure
 * input. Keep new fields additive so stored `games.match_format` rows stay
 * valid as the engine's needs get firmer in Phase 3.
 */

/** How the deciding (last) set of a match is resolved. */
export type DecidingSetRule = 'full_set' | 'match_tiebreak' | 'none';

export interface TennisMatchFormat {
  /** Stable id, stored in games.match_format and never renamed once shipped. */
  id: string;
  label: string;
  /** Number of sets that decide the match (2 = best of 3, 3 = best of 5, 1 = single pro set). */
  sets: number;
  /** Games needed to win a non-deciding set. */
  gamesPerSet: number;
  /** Game score (e.g. 6 for 6-6) that triggers a set tiebreak. */
  tiebreakAt: number;
  decidingSet: DecidingSetRule;
  /** Only meaningful when decidingSet === 'match_tiebreak'. */
  decidingTiebreakPoints?: 7 | 10;
  /** No-ad (sudden-death deuce) scoring. */
  noAd: boolean;
}

/** Default first, per the spec's format table. */
export const TENNIS_MATCH_FORMATS: TennisMatchFormat[] = [
  {
    id: 'best_of_3_match_tb',
    label: 'Best of 3, match TB 3rd',
    sets: 2,
    gamesPerSet: 6,
    tiebreakAt: 6,
    decidingSet: 'match_tiebreak',
    decidingTiebreakPoints: 10,
    noAd: false,
  },
  {
    id: 'best_of_3_full_3rd',
    label: 'Best of 3, full 3rd',
    sets: 2,
    gamesPerSet: 6,
    tiebreakAt: 6,
    decidingSet: 'full_set',
    noAd: false,
  },
  {
    id: 'short_sets_to_4',
    label: 'Short sets to 4',
    sets: 2,
    gamesPerSet: 4,
    tiebreakAt: 4,
    decidingSet: 'match_tiebreak',
    decidingTiebreakPoints: 10,
    noAd: false,
  },
  {
    id: 'pro_set_8',
    label: '8-game pro set',
    sets: 1,
    gamesPerSet: 8,
    tiebreakAt: 8,
    decidingSet: 'none',
    noAd: false,
  },
  {
    id: 'fast4',
    label: 'Fast4',
    sets: 2,
    gamesPerSet: 4,
    tiebreakAt: 3,
    decidingSet: 'match_tiebreak',
    decidingTiebreakPoints: 7,
    noAd: true,
  },
  {
    id: 'best_of_5',
    label: 'Best of 5',
    sets: 3,
    gamesPerSet: 6,
    tiebreakAt: 6,
    decidingSet: 'full_set',
    noAd: false,
  },
];

export const DEFAULT_TENNIS_MATCH_FORMAT = TENNIS_MATCH_FORMATS[0];

export function getTennisMatchFormat(id: string | undefined | null): TennisMatchFormat {
  return TENNIS_MATCH_FORMATS.find((f) => f.id === id) ?? DEFAULT_TENNIS_MATCH_FORMAT;
}
